import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import Deal from '@r6digital/rapidstor-types/types/Deal';
import Unit from '@r6digital/rapidstor-types/types/Unit';

import { createSlice, createSelector } from '@reduxjs/toolkit';
import { UnitTypeWithImages } from '@/standard-unit-listing/screens';

import { FormType } from '@/core/components/elements/contact-card';
import type {
    UpsellFunnel,
    UpsellFunnelSnapshot,
} from '@/journey-based-listing/utils/customiseBookingTracking';

import { RootState } from './index';

type ResponseType = string | boolean | number;

export interface UnitData {
    location: Location;
    unitType: UnitType;
    descriptors: Descriptor[];
    descriptor: Descriptor;
}

export interface ContactData {
    neededDate?: string;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
}

export interface ReservationData {
    unitType: UnitType;
    unitToUse?: Unit;
    descriptor: Descriptor;
    coupon?: string | null;
    dealObject?: Deal;
    deal?: string;
    contactData?: ContactData;
    token?: string;
}

export interface ContactCardState {
    animate: string;
    type: FormType;
    unitType: UnitType | null;
    isDeal?: boolean;
    deal?: string | null;
}

export interface DetailsModalState {
    unitType: UnitTypeWithImages | null;
    type: 'bookNow' | 'contactMe' | 'showPrices' | null;
    isOpen: boolean;
}

export interface SelectedDealState {
    deal: string | null;
    dealObject: Deal | null;
}

interface State {
    unitData: UnitData | null;
    upSell: Descriptor | null;
    reservationData: ReservationData | null;
    /**
     * The deal the user last picked, independent of `reservationData`. The
     * standard unit listing lets a deal be chosen before any reservation
     * exists, so the selection cannot live on `reservationData` alone.
     */
    selectedDeal: SelectedDealState;
    contactData: ContactData | null;
    responses: Record<string, ResponseType | ResponseType[]>;
    locationCode: string;
    contactCard: ContactCardState;
    detailsModal: DetailsModalState;
    personaId: string | undefined;
    sizesViewed: string[];
    /**
     * The upsell funnel for one customise-booking visit. `base` is the anchor: the
     * selection the visitor landed on, kept because taking an upsell replaces the
     * descriptor and nothing else remembers where they started.
     */
    upsellFunnel: UpsellFunnel | null;
}

const initialState: State = {
    unitData: null,
    upSell: null,
    reservationData: null,
    selectedDeal: {
        deal: null,
        dealObject: null,
    },
    contactData: null,
    responses: {},
    locationCode: '',
    contactCard: {
        animate: 'initial',
        type: 'contactMe',
        unitType: null,
        isDeal: false,
        deal: null,
    },
    detailsModal: {
        unitType: null,
        isOpen: false,
        type: null,
    },
    personaId: undefined,
    sizesViewed: [],
    upsellFunnel: null,
};

export const bookingSlice = createSlice({
    name: 'booking',
    initialState,
    reducers: {
        setUnitData: (
            state,
            action: { payload: UnitData | null; type: string },
        ) => {
            state.unitData = action.payload;
        },
        setUpsell: (
            state,
            action: { payload: Descriptor | null; type: string },
        ) => {
            state.upSell = action.payload;
        },
        setReservationData: (
            state,
            action: { payload: ReservationData | null; type: string },
        ) => {
            state.reservationData = action.payload;
        },
        setContactData: (
            state,
            action: { payload: ContactData | null; type: string },
        ) => {
            state.contactData = action.payload;
        },
        setResponse: (state, action) => {
            const { id, response } = action.payload;
            state.responses[id] = response;
        },
        setLocationCode: (state, action) => {
            const { locationCode } = action.payload;
            state.locationCode = locationCode;
        },
        setContactCard: (
            state,
            action: { payload: ContactCardState; type: string },
        ) => {
            state.contactCard = action.payload;
        },
        setDetailsModal: (
            state,
            action: { payload: DetailsModalState; type: string },
        ) => {
            state.detailsModal = action.payload;
        },
        resetState: (state) => {
            Object.assign(state, initialState);
        },
        setPersonaId: (state, action: { payload: string; type: string }) => {
            state.personaId = action.payload;
        },
        setSizesViewed: (state, action: { payload: string; type: string }) => {
            if (!state.sizesViewed.includes(action.payload)) {
                state.sizesViewed.push(action.payload);
            }
        },
        setUpsellFunnel: (
            state,
            action: { payload: UpsellFunnelSnapshot; type: string },
        ) => {
            state.upsellFunnel = { ...action.payload, selections: [] };
        },
        addUpsellSelection: (state, action: { payload: string; type: string }) => {
            if (
                state.upsellFunnel
                && !state.upsellFunnel.selections.includes(action.payload)
            ) {
                state.upsellFunnel.selections.push(action.payload);
            }
        },
        clearUpsellFunnel: (state) => {
            state.upsellFunnel = null;
        },
        setDeal: (
            state,
            action: { payload: { deal: string | null; dealObject: Deal | undefined }; type: string },
        ) => {
            const nextDeal = action.payload.deal ?? undefined;
            const nextDealObject = nextDeal ? action.payload.dealObject : undefined;

            // Always record the selection, even before a reservation exists —
            // the standard unit listing picks a deal straight from the price
            // cards, well before `reservationData` is populated.
            state.selectedDeal = {
                deal: action.payload.deal ?? null,
                dealObject: nextDealObject ?? null,
            };

            if (state.reservationData) {
                state.reservationData.deal = nextDeal;
                state.reservationData.dealObject = nextDealObject;
            }
        },
    },
});

export const {
    setUnitData,
    setUpsell,
    setReservationData,
    setContactData,
    setResponse,
    setLocationCode,
    setContactCard,
    resetState,
    setPersonaId,
    setDetailsModal,
    setSizesViewed,
    setDeal,
    setUpsellFunnel,
    addUpsellSelection,
    clearUpsellFunnel,
} = bookingSlice.actions;

export const selectUnitData = (state: RootState) => state.booking.unitData;
export const selectUpsell = (state: RootState) => state.booking.upSell;
export const selectReservationData = (state: RootState) => state.booking.reservationData;
export const selectContactData = (state: RootState) => state.booking.contactData;
export const selectResponses = (state: RootState) => state.booking.responses;
export const selectLocationCode = (state: RootState) => state.booking.locationCode;
export const selectContactCard = (state: RootState) => state.booking.contactCard;
export const selectPersonaId = (state: RootState) => state.booking.personaId;
export const selectDetailsModal = (state: RootState) => state.booking.detailsModal;
export const selectSizesViewed = (state: RootState) => state.booking.sizesViewed;
export const selectSelectedDeal = (state: RootState) => state.booking.selectedDeal;
export const selectUpsellFunnel = (state: RootState) => state.booking.upsellFunnel;
export const selectCurrentDeal = createSelector(
    (state: RootState) => state.booking.reservationData,
    (reservationData) => ({
        dealId: reservationData?.deal ?? null,
        dealObject: reservationData?.dealObject ?? null,
    }),
);
